// redux/user/profilePictures/userProfilePicturesThunks.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import * as ProfilePictureAPI from './userProfilePicturesAPI';

// Track pending requests to prevent duplicates
const pendingRequests = new Map();

// FETCH - with graceful 404 handling and deduplication
export const fetchProfilePicture = createAsyncThunk(
  'userProfilePictures/fetch',
  async (userId, { rejectWithValue, getState }) => {
    // Check if we already have this picture
    const state = getState();
    const existingPicture = state.userProfilePictures?.profilePictures?.[userId];
    if (existingPicture !== undefined) {
      return { userId, pictureUrl: existingPicture };
    }

    // Check if there's already a pending request for this user
    const pendingKey = `fetch-${userId}`;
    if (pendingRequests.has(pendingKey)) {
      try {
        // Wait for the existing request to complete
        const result = await pendingRequests.get(pendingKey);
        return result;
      } catch (err) {
        // If the pending request failed, continue with a new one
      }
    }

    // Create new request promise
    const requestPromise = (async () => {
      try {
        const pictureUrl = await ProfilePictureAPI.fetch(userId);
        const result = { userId, pictureUrl };
        pendingRequests.delete(pendingKey);
        return result;
      } catch (err) {
        pendingRequests.delete(pendingKey);
        if (err?.response?.status === 404) {
          return { userId, pictureUrl: null };
        }
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err.message ||
          'Failed to fetch profile picture';
        throw { userId, error: msg };
      }
    })();

    // Store the promise to prevent duplicate requests
    pendingRequests.set(pendingKey, requestPromise);
    
    try {
      return await requestPromise;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// CREATE
export const createProfilePicture = createAsyncThunk(
  'profilePicture/create',
  async ({ userId, fileOrUrl }, { rejectWithValue }) => {
    try {
      const pictureUrl = await ProfilePictureAPI.create(userId, fileOrUrl);
      return { userId, pictureUrl };
    } catch (error) {
      return rejectWithValue({
        userId,
        error: error.response?.data?.message || error.message || 'Failed to create profile picture',
      });
    }
  }
);

// UPDATE
export const updateProfilePicture = createAsyncThunk(
  'profilePicture/update',
  async ({ userId, fileOrUrl }, { rejectWithValue }) => {
    try {
      const pictureUrl = await ProfilePictureAPI.update(userId, fileOrUrl);
      return { userId, pictureUrl };
    } catch (error) {
      return rejectWithValue({
        userId,
        error: error.response?.data?.message || error.message || 'Failed to update profile picture',
      });
    }
  }
);

// DELETE
export const deleteProfilePicture = createAsyncThunk(
  'profilePicture/delete',
  async (userId, { rejectWithValue }) => {
    try {
      await ProfilePictureAPI.del(userId);
      return { userId };
    } catch (error) {
      return rejectWithValue({
        userId,
        error: error.response?.data?.message || error.message || 'Failed to delete profile picture',
      });
    }
  }
);

// Utility function to clear pending requests (useful for cleanup)
export const clearPendingRequests = () => {
  pendingRequests.clear();
};